import { useState, type ReactNode } from 'react'
import { Download, Loader2, Lock } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Toggle } from '~/components/ui/toggle'
import {
    STATUS_LABEL,
    STATUS_TONE,
    type DocumentRow,
} from '~/components/knowledge/UploadManagementSection'
import { formatDateTime } from '~/lib/utils'

// 업로드 문서(Postgres documents) 원본 파일 정보 슬라이드오버. 업로드 관리 표의 행 클릭으로 열려
// 제목·도메인·상태·재시도·생성일·원본 파일명·오류·메타를 읽고, 원본 파일을 어드민 백엔드 프록시로
// 다운로드한다. 수동 관리 잠금 토글로 재색인이 이 문서를 건너뛰게 해 코퍼스 청크 수동 편집·삭제를
// 지킨다. 코퍼스 청크 드릴다운(KnowledgeDocumentPanel)과 같은 우측 드로어 패턴.
export function DocumentDetailPanel({
    doc,
    onClose,
    onDownload,
    onToggleLock,
}: {
    doc: DocumentRow
    onClose: () => void
    onDownload: () => Promise<void>
    // 수동 관리 잠금 토글(잠금 ON이면 재색인이 이 문서를 건너뛴다). 페이지가 API 호출·상태 갱신.
    onToggleLock: (locked: boolean) => Promise<void>
}) {
    const [downloading, setDownloading] = useState(false)
    const [lockBusy, setLockBusy] = useState(false)

    const handleDownload = async () => {
        setDownloading(true)
        try {
            await onDownload()
        } finally {
            setDownloading(false)
        }
    }

    // 잠금 상태는 부모(doc)를 진실원으로 삼는다(토글 후 페이지가 doc을 갱신하면 반영된다).
    const locked =
        doc.curationLocked ?? doc.metadata?.curation_locked === 'true'

    const handleToggleLock = async (next: boolean) => {
        setLockBusy(true)
        try {
            await onToggleLock(next)
        } finally {
            setLockBusy(false)
        }
    }

    // 원본 파일명은 업로드 시 저장한 metadata.filename을 우선하고, 없으면 스토리지 key 마지막 조각.
    const originalName =
        doc.metadata?.filename ?? doc.sourceKey?.split('/').pop() ?? '—'
    const contentType = doc.metadata?.content_type

    return (
        <div className='fixed inset-0 z-50 flex justify-end'>
            <button
                type='button'
                aria-label='닫기'
                className='bg-background/60 absolute inset-0'
                onClick={onClose}
            />
            <div className='border-border bg-card relative flex h-full w-full max-w-md flex-col border-l shadow-lg'>
                <div className='border-border flex items-start justify-between gap-3 border-b p-4'>
                    <div className='min-w-0'>
                        <div className='admin-eyebrow'>업로드 문서</div>
                        <h2 className='krds-title text-foreground truncate'>
                            {doc.title}
                        </h2>
                        <div className='mt-1.5'>
                            <Badge tone={STATUS_TONE[doc.status] ?? 'neutral'}>
                                {STATUS_LABEL[doc.status] ?? doc.status}
                            </Badge>
                        </div>
                    </div>
                    <Button variant='ghost' size='sm' onClick={onClose}>
                        닫기
                    </Button>
                </div>

                <div className='flex-1 overflow-y-auto p-4'>
                    <dl className='text-sm'>
                        <Field label='제목'>{doc.title}</Field>
                        <Field label='도메인'>{doc.domain}</Field>
                        <Field label='상태'>
                            <Badge tone={STATUS_TONE[doc.status] ?? 'neutral'}>
                                {STATUS_LABEL[doc.status] ?? doc.status}
                            </Badge>
                        </Field>
                        <Field label='재시도'>{doc.retryCount}</Field>
                        <Field label='생성일'>
                            {formatDateTime(doc.createdAt)}
                        </Field>
                        <Field label='원본 파일명'>{originalName}</Field>
                        {contentType ? (
                            <Field label='파일 형식'>{contentType}</Field>
                        ) : null}
                        {doc.sourceKey ? (
                            <Field label='스토리지 키'>
                                <span className='break-all'>
                                    {doc.sourceKey}
                                </span>
                            </Field>
                        ) : null}
                        {doc.error ? (
                            <Field label='오류'>
                                <span className='text-destructive'>
                                    {doc.error}
                                </span>
                            </Field>
                        ) : null}
                    </dl>
                </div>

                <div className='border-border space-y-3 border-t p-4'>
                    {/* 수동 관리 잠금: 잠그면 재색인이 이 문서를 건너뛰어 수동 편집이 유지된다. */}
                    <div className='bg-background border-border rounded-[var(--radius)] border p-3'>
                        <div className='flex items-center justify-between gap-3'>
                            <div className='flex items-center gap-1.5'>
                                <Lock className='text-muted-foreground size-3.5' />
                                <span className='text-foreground text-[13px] font-medium'>
                                    수동 관리 잠금
                                </span>
                                {locked ? (
                                    <Badge tone='warning'>잠김</Badge>
                                ) : null}
                            </div>
                            <Toggle
                                checked={locked}
                                onCheckedChange={handleToggleLock}
                                disabled={lockBusy}
                                aria-label='수동 관리 잠금'
                            />
                        </div>
                        <p className='text-muted-foreground mt-1.5 text-[11px]'>
                            잠그면 재색인이 이 문서를 건너뛰어 수동 편집이
                            유지돼요.
                        </p>
                    </div>

                    <Button
                        className='w-full'
                        onClick={handleDownload}
                        disabled={downloading}>
                        {downloading ? (
                            <Loader2 className='animate-spin' />
                        ) : (
                            <Download />
                        )}
                        원본 다운로드
                    </Button>
                </div>
            </div>
        </div>
    )
}

// 라벨·값 한 줄(각진 고밀도 정의 목록).
function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className='border-border/60 grid grid-cols-[6rem_1fr] gap-3 border-b py-2'>
            <dt className='text-muted-foreground text-[12px]'>{label}</dt>
            <dd className='text-foreground text-[13px]'>{children}</dd>
        </div>
    )
}
